





































import { PostColor } from 'types'
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator'
import { namespace, State, Getter } from 'vuex-class'
import ImageThumbnail from 'library/v4/components/image-thumbnail.vue'
import BeethovenAttachmentMetadataSlope from 'vuecomponents/beethoven-attachment-metadata-slope.vue'

import { getDisplayAttributes } from 'bits/beethoven'
import { BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import { getHostname } from 'bits/url'

import FallbackFile from 'images/fallback_file.png' // 1439 × 756
import { IntlMixin } from 'vuemixins/intl_mixin'

function getStylizedProviderName(name): string {
  if (!name) return ''
  // This is done to comply with Youtube's requirements in order to use their player on Padlet.
  if (name.toLowerCase() === 'youtube') return name
  if (name.toLowerCase() === 'instagram') return name
  return name.toLowerCase()
}

/**
 * @displayName Beethoven Attachment Preview
 * Barebone component that fetches Beethoven Data and loads the preview image:
 * 1. Fetches preview info from beethoven.
 * 2. Preview Data is loaded, emit `load-data` then load image using image-thumbnail
 * 3. Image is loaded, emit `load-image`
 */
@Component({ components: { ImageThumbnail, BeethovenAttachmentMetadataSlope } })
export default class BeethovenAttachmentPreview extends Mixins(IntlMixin) {
  @Prop() url!: string
  @Prop({ default: 508 }) width!: number
  @Prop({ default: undefined }) height!: number | undefined
  /**
   * Show border in case the preview is loaded on a background that requires it so that it won't blend in too much.
   * For example set this to true if loading a PDF on the white modal.
   */
  @Prop({ default: false }) xBorder!: boolean
  /**
   * Default to show metadata slope, can hide if needed
   */
  @Prop({ default: true }) xMetadata!: boolean
  /**
   * How the component should decide to show dark mode: Using browser or with custom isDark prop
   */
  @Prop({ default: 'browser' }) darkThemeType!: 'browser' | 'custom'
  @Prop({ default: 'browser' }) isDark!: false
  @Prop({ default: true }) lazyLoading!: boolean
  @Prop({ default: 'default' }) backgroundColor!: 'default' | NonNullable<PostColor>
  @Prop({ default: '' }) borderRadiusClass!: string
  @State isMobile!: boolean

  @State dpr!: number
  @Getter useArvo!: boolean
  @(namespace('browser').Getter) isPhone!: boolean
  @(namespace('attachment').Action) updateLinkDisplayAttributes

  link: BeethovenDisplayAttributes | null = null
  isLoaded = false
  isError = false
  isImageLoaded = false
  isFile = false

  // Note: need to initialize these values for reactivity of getter based on them to work
  imageUrl: string | null = null
  // Default to size of fallback image (images/fallback_file.png)
  thumbnailWidth = 1439
  thumbnailHeight = 756

  get isImage(): boolean {
    return this.link?.content_category === 'photo'
  }

  get isWebpage(): boolean {
    return this.link?.content_category === 'page'
  }

  get isPadlet(): boolean {
    return this.link?.content_subcategory === 'padlet'
  }

  get isYoutube(): boolean {
    return this.link?.provider_name.toLowerCase() === 'youtube'
  }

  get isInstagram(): boolean {
    return this.link?.provider_name.toLowerCase() === 'instagram'
  }

  get isLoadingFallbackImage(): boolean {
    return this.isError || (!!this.imageUrl && this.imageUrl.startsWith('data:'))
  }

  get previewImageSrc(): string {
    if (!!this.imageUrl && !this.isLoadingFallbackImage) return this.imageUrl
    if (!this.link) return FallbackFile
    // TODO: Add other fallback files
    return FallbackFile
  }

  get hasMetadata(): boolean {
    if (!this.link) return false
    if (this.isInstagram) return true
    return !this.isImage && this.isImageLoaded
  }

  get metadataText(): string {
    if (!this.link) return ''
    if (this.isPadlet) return this.__('Padlet') + ' · ' + getStylizedProviderName(this.link.provider_name)
    // if (this.isYoutube) return getStylizedProviderName(this.link.provider_name) + ' · ' + 'insert duration here' //TODO: Links currently doesn't have duration, we need to save them to be able to show
    if (this.isFile && this.link.metadata?.pretty_size)
      return this.link.extension.toUpperCase() + ' · ' + this.link.metadata.pretty_size
    if (this.isWebpage) {
      return (
        getHostname(this.link.url).replace(/^www\./, '') ||
        getStylizedProviderName(this.link.provider_name) ||
        this.__('webpage')
      )
    }
    return this.link.extension.toUpperCase() || getStylizedProviderName(this.link.provider_name) || ''
  }

  @Watch('width')
  handleWidthChanged(): void {
    this.getBeethovenAttributes()
  }

  @Watch('url')
  handleAttachmentUrlChanged(): void {
    this.getBeethovenAttributes()
  }

  useSizeDataForFallbackImage(): void {
    this.thumbnailWidth = 1439
    this.thumbnailHeight = 756
  }

  handleBeethovenError(error): void {
    this.$emit('error', error)
    this.isError = true
    this.useSizeDataForFallbackImage()
  }

  async getBeethovenAttributes(): Promise<void> {
    const locale = 'en'
    const bhOptions = {
      dpr: this.dpr,
      locale: locale,
      width: this.width,
      height: this.height,
      useArvo: this.useArvo,
    }

    let data
    try {
      data = await getDisplayAttributes(this.url, bhOptions)
      if (data === null) {
        this.handleBeethovenError('No data.')
      }
      if (data.error) {
        this.handleBeethovenError(data.error)
      }
    } catch (error) {
      this.handleBeethovenError(error)
    }

    if (this.isError) return

    this.updateLinkDisplayAttributes({ url: this.url, attributes: data })
    this.link = data
    this.isLoaded = true
    this.$emit('load-data', data)
    this.isFile = data.content_category == 'file'
    this.imageUrl = data.image_url
    this.thumbnailWidth = data.image_width
    this.thumbnailHeight = data.image_height
  }

  handleImageLoaded(): void {
    this.isImageLoaded = true
    this.$emit('load-image')
  }

  mounted(): void {
    this.getBeethovenAttributes()
  }
}
